import * as React from "react";
import { useEffect } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import Divider from "@mui/material/Divider";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { UserContext } from "../../App";
import Plotting from "../Plotting";

export default function AFSTrainingStatus(props) {
  const theme = useTheme();
  // init internationalization
  const { t } = useTranslation();
  // get experiment methods
  const {
    datasets,
    currentDatasetIdx,
    experiments,
    currentExperimentIdx,
    fetchExperiments,
  } = React.useContext(UserContext);

  // needed for routing to inspection site
  const navigate = useNavigate();

  const handleFinish = () => {
    navigate("/analyze");
  };

  const [isAFSComplete, setIsAFSComplete] = React.useState(false);
  /* SOCKET CONNECTION
    The socket.io connection is used, to update the processing progress
    */
  const [progressData, setProgressData] = React.useState({
    selectedFeaturesProgress: {},
    progressStats: {},
  });

  // connect to socket, when the current dataset is gathered
  useEffect(() => {
    props.socket.connect();
    props.socket.on("connect", () => {
      const roomData = { ...props.pipelineRoom };
      roomData.runName = `${props.pipelineRoom.runName}_${props.stepName}`;
      // this method will be triggered by the server
      props.socket.on("update-afs-training", (data) => {
        const trainingData = data;
        // if final data is available, add it to progressStats and selectedFeaturesProgress
        if (Object.keys(data.finalStats).length > 0) {
          trainingData.progressStats = {
            ...data.progressStats,
            ...data.finalStats,
          };
          trainingData.selectedFeaturesProgress = {
            ...data.selectedFeaturesProgress,
            ...data.finalStats,
          };
        }
        setProgressData(trainingData);
      });
      props.socket.on("afs-training-completed", () => {
        setIsAFSComplete(true);
        fetchExperiments({ latest: props.experimentName });
      });

      // create room for current user
      props.socket.emit("create-afs-training-room", roomData);
    });
  }, [
    datasets,
    currentDatasetIdx,
    experiments,
    currentExperimentIdx,
    props.stepName,
  ]);

  // Step Details
  const DetailsAccordion = styled(Accordion)(() => ({
    backgroundColor: "transparent",
    boxShadow: theme.shadows[0],
    "&.Mui-expanded": {
      backgroundColor: "transparent",
    },
    "&.MuiDetailsAccordionSummary": {
      flexDirection: "row-reverse",
    },
  }));

  const DetailsAccordionSummary = styled(AccordionSummary)(() => ({
    flexDirection: "row-reverse",
  }));

  return (
    <Box id="processing-status-container" sx={{ padding: theme.spacing(4, 1) }}>
      <Typography variant="h4" gutterBottom>
        {t("AFSTrainingStatus.PageTitle")}
        <br />
      </Typography>
      <Typography variant="body2">
        {t("AFSTrainingStatus.PageDescription")}
      </Typography>
      <br />

      <Grid
        container
        spacing={2}
        direction="row"
        justifyContent="flex-start"
        alignItems="start"
      >
        <Grid item xl={12} xs={12}>
          <DetailsAccordion defaultExpanded>
            <DetailsAccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="details2-content"
              id="details2-header"
            >
              {t("AFSTrainingStatus.DescriptionLossCurve")}
            </DetailsAccordionSummary>
            {Object.keys(progressData.progressStats).length > 0 ? (
              <AccordionDetails>
                <Grid item lg={12} xs={12} sx={{ minWidth: "800px" }}>
                  <Divider />
                  <Plotting
                    runData={progressData.progressStats}
                    type="AFSScatter"
                  />
                </Grid>
              </AccordionDetails>
            ) : (
              ""
            )}
          </DetailsAccordion>

          <DetailsAccordion defaultExpanded>
            <DetailsAccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="details1-content"
              id="details1-header"
            >
              <Typography variant="h7" color="text.secondary">
                {t("AFSTrainingStatus.DescriptionSelectedFeatures")}
              </Typography>
            </DetailsAccordionSummary>
            <AccordionDetails>
              {Object.keys(progressData.selectedFeaturesProgress).length > 0 ? (
                <Plotting
                  runData={progressData.selectedFeaturesProgress}
                  type="AFSHeatmap"
                />
              ) : (
                ""
              )}
            </AccordionDetails>
          </DetailsAccordion>

          {isAFSComplete ? (
            <Paper
              square
              elevation={0}
              sx={{ p: 3, marginTop: theme.spacing(3), background: grey[100] }}
            >
              <Typography>{t("trainingStatus.CompletedMessage")}</Typography>
              <Button
                onClick={handleFinish}
                sx={{ mt: 1, mr: 1 }}
                variant="contained"
                color="primary"
              >
                {t("trainingStatus.ProceedButton")}
              </Button>
            </Paper>
          ) : (
            ""
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
